import { Injectable, inject } from '@angular/core';
import { faro } from '@grafana/faro-web-sdk';
import { AuthService } from './auth.service';
import { AbstractControl } from '@angular/forms';
import { OfferingType } from '../enums/offering-type.enum';

interface ErrorField {
  field_id: string;
  field_error: string;
  error_count: number;
}

interface FormValues {
  formOverview: any;
  formDetails: any;
  formFunctionality: any;
  formPermissions: any;
}

interface KnowledgeSection {
  name: string;
  isVisible: boolean;
  rowCount: number;
  description?: string;
  data?: any[];
}

interface KnowledgeSectionChange {
  section: string;
  rows_added?: number;
  rows_deleted?: number;
  rows_changed?: number;
  description_changed?: boolean;
  visibility_changed_to?: 'enabled' | 'disabled';
}

interface KnowledgeValues {
  sections: KnowledgeSection[];
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly auth = inject(AuthService);

  /**
   * Single exit point for every analytics event.
   *
   * `sub` also travels as Faro session user meta, but the transport sends one
   * meta per batch — `getTransportBody` uses `item[0].meta` for up to 50
   * events — so an event pushed after sign-in can inherit the attribution of
   * an earlier one in the same 250ms window. Stamping the claim on the payload
   * makes each event self-describing and queryable on its own.
   *
   * Only the opaque claim goes in. Payloads carry field *ids*, never field
   * values; no email, username or display name may ever be added here.
   */
  private push(eventName: string, payload: Record<string, unknown>): void {
    if (!faro?.api?.pushEvent) {
      return;
    }
    faro.api.pushEvent(eventName, {
      ...payload,
      ping_sub: this.auth.sub() ?? 'guest-unknown',
    });
  }

  private errorCounts: Record<string, number> = {};
  private originalFormValues: FormValues | null = null;
  private _cachedChangedFields: string[] | null = null;
  private _cachedAddedFields: string[] | null = null;
  private changedFields: string[] = [];
  private addedFields: string[] = [];
  private originalKnowledgeValues: KnowledgeValues | null = null;
  private _cachedKnowledgeSectionChanges: KnowledgeSectionChange[] | null =
    null;

  // Track initial form values
  setOriginalFormValues(formValues: FormValues): void {
    this.originalFormValues = formValues;
    this._cachedChangedFields = null;
    this._cachedAddedFields = null;
    this.changedFields = [];
    this.addedFields = [];
  }

  // Invalidate cache when form changes
  invalidateCache(): void {
    this._cachedChangedFields = null;
    this._cachedAddedFields = null;
  }

  // Helper to check if a value is empty
  private isEmpty(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    return false;
  }

  // Get changed and added fields with caching
  private getFieldChanges(currentFormValues: FormValues): {
    changedFields: string[];
    addedFields: string[];
  } {
    if (
      this._cachedChangedFields !== null &&
      this._cachedAddedFields !== null
    ) {
      return {
        changedFields: this._cachedChangedFields,
        addedFields: this._cachedAddedFields,
      };
    }

    if (!this.originalFormValues) {
      this._cachedChangedFields = [];
      this._cachedAddedFields = [];
      return { changedFields: [], addedFields: [] };
    }

    this.changedFields = [];
    this.addedFields = [];

    for (const section in this.originalFormValues) {
      if (section in currentFormValues) {
        const sectionKey = section as keyof FormValues;
        const originalFields = this.originalFormValues[sectionKey];
        const currentFields = currentFormValues[sectionKey];

        const fieldKeys = Object.keys(originalFields);
        for (const field of fieldKeys) {
          const typedField = field as keyof typeof originalFields;
          const originalValue = originalFields[typedField];
          const currentValue = currentFields[typedField];

          const originalEmpty = this.isEmpty(originalValue);
          const currentEmpty = this.isEmpty(currentValue);

          // If values are different
          if (JSON.stringify(originalValue) !== JSON.stringify(currentValue)) {
            // If original was empty and now has value → added
            if (originalEmpty && !currentEmpty) {
              this.addedFields.push(field);
            }
            // If original had value and now has different value → changed
            else if (!originalEmpty && !currentEmpty) {
              this.changedFields.push(field);
            }
            // If original had value and now is empty → changed (cleared)
            else if (!originalEmpty && currentEmpty) {
              this.changedFields.push(field);
            }
          }
        }
      }
    }

    this._cachedChangedFields = this.changedFields;
    this._cachedAddedFields = this.addedFields;
    return { changedFields: this.changedFields, addedFields: this.addedFields };
  }

  // Public method to get changed fields (for backward compatibility)
  getChangedFields(currentFormValues: FormValues): string[] {
    return this.getFieldChanges(currentFormValues).changedFields;
  }

  // Public method to get added fields
  getAddedFields(currentFormValues: FormValues): string[] {
    return this.getFieldChanges(currentFormValues).addedFields;
  }

  // Track errors on demand
  trackErrorsOnDemand(formControls: Record<string, AbstractControl>): void {
    const entries = Object.entries(formControls);
    for (const [fieldId, control] of entries) {
      if (control.errors) {
        this.errorCounts[fieldId] = (this.errorCounts[fieldId] || 0) + 1;
      }
    }
  }

  // Get error fields
  private getErrorFields(formControls: Record<string, AbstractControl>): {
    errorFieldsCount: number;
    errorFields: ErrorField[];
  } {
    const errorFields: ErrorField[] = [];

    const controlKeys = Object.keys(formControls);
    for (const fieldId of controlKeys) {
      const control = formControls[fieldId];
      if (control?.errors) {
        const errorKeys = Object.keys(control.errors);
        for (const errorKey of errorKeys) {
          errorFields.push({
            field_id: fieldId,
            field_error: errorKey,
            error_count: this.errorCounts[fieldId] || 0,
          });
        }
      }
    }

    return {
      errorFieldsCount: errorFields.length,
      errorFields,
    };
  }

  // Calculate total fields
  private calculateTotalFields(formValues: FormValues): number {
    return (
      Object.keys(formValues.formOverview).length +
      Object.keys(formValues.formDetails).length +
      Object.keys(formValues.formFunctionality).length +
      Object.keys(formValues.formPermissions).length
    );
  }

  // Push form entry event
  pushFormEntryEvent(
    offeringId: string,
    offeringType: OfferingType,
    formId: string = 'offering_form',
  ): void {
    const formEntryPayload = {
      event_name: 'form_entry',
      offering_id: offeringId,
      offering_type: offeringType,
      form_id: formId,
    };

    this.push('form_entry', formEntryPayload);
  }

  // Push form success event
  pushFormSuccessEvent(
    offeringId: string,
    currentFormValues: FormValues,
    offeringType: OfferingType,
    formId: string = 'offering_form',
  ): void {
    const { changedFields, addedFields } =
      this.getFieldChanges(currentFormValues);
    const changedFieldsCount = changedFields.length;
    const addedFieldsCount = addedFields.length;
    const totalFields = this.calculateTotalFields(currentFormValues);
    const unchangedFieldsCount =
      totalFields - changedFieldsCount - addedFieldsCount;

    const basePayload: any = {
      event_name: 'form_success',
      offering_id: offeringId,
      offering_type: offeringType,
      form_id: formId,
      fields_count: totalFields.toString(),
      unchanged_fields_count: unchangedFieldsCount.toString(),
      changed_fields_count: changedFieldsCount.toString(),
      added_fields_count: addedFieldsCount.toString(),
    };

    if (changedFieldsCount > 0) {
      basePayload.changed_fields = JSON.stringify(
        changedFields.map((field) => ({ field_id: field })),
      );
    }

    if (addedFieldsCount > 0) {
      basePayload.added_fields = JSON.stringify(
        addedFields.map((field) => ({ field_id: field })),
      );
    }

    this.push('form_success', basePayload);
  }

  // Push manual draft save event
  pushDraftSaveEvent(
    offeringId: string,
    currentFormValues: FormValues,
    formControls: Record<string, AbstractControl>,
    fieldErrorHistory: Map<string, number>,
    offeringType: OfferingType,
    formId: string = 'offering_form',
  ): void {
    this.trackErrorsOnDemand(formControls);
    const { changedFields, addedFields } =
      this.getFieldChanges(currentFormValues);
    const changedFieldsCount = changedFields.length;
    const addedFieldsCount = addedFields.length;
    const totalFields = this.calculateTotalFields(currentFormValues);
    const unchangedFieldsCount =
      totalFields - changedFieldsCount - addedFieldsCount;

    const basePayload: any = {
      event_name: 'form_draft_save',
      offering_id: offeringId,
      offering_type: offeringType,
      form_id: formId,
      fields_count: totalFields.toString(),
      unchanged_fields_count: unchangedFieldsCount.toString(),
      changed_fields_count: changedFieldsCount.toString(),
      added_fields_count: addedFieldsCount.toString(),
      error_fields_count: Array.from(fieldErrorHistory.values())
        .reduce((sum, count) => sum + count, 0)
        .toString(),
    };

    if (changedFieldsCount > 0) {
      basePayload.changed_fields = JSON.stringify(
        changedFields.map((field) => ({ field_id: field })),
      );
    }

    if (addedFieldsCount > 0) {
      basePayload.added_fields = JSON.stringify(
        addedFields.map((field) => ({ field_id: field })),
      );
    }

    if (fieldErrorHistory.size > 0) {
      basePayload.error_fields = JSON.stringify(
        Array.from(fieldErrorHistory.entries()).map(([field_id, count]) => ({
          field_id,
          error_count: count,
        })),
      );
    }

    this.push(basePayload.event_name, basePayload);
  }

  // Push form error event
  pushFormErrorEvent(
    offeringId: string,
    currentFormValues: FormValues,
    formControls: Record<string, AbstractControl>,
    fieldErrorHistory: Map<string, number>,
    offeringType: OfferingType,
    formId: string = 'offering_form',
  ): void {
    this.trackErrorsOnDemand(formControls);
    const { changedFields, addedFields } =
      this.getFieldChanges(currentFormValues);
    const changedFieldsCount = changedFields.length;
    const addedFieldsCount = addedFields.length;
    const totalFields = this.calculateTotalFields(currentFormValues);
    const unchangedFieldsCount =
      totalFields - changedFieldsCount - addedFieldsCount;

    const basePayload: any = {
      event_name: 'form_errors',
      offering_id: offeringId,
      offering_type: offeringType,
      form_id: formId,
      fields_count: totalFields.toString(),
      unchanged_fields_count: unchangedFieldsCount.toString(),
      changed_fields_count: changedFieldsCount.toString(),
      added_fields_count: addedFieldsCount.toString(),
      error_fields_count: Array.from(fieldErrorHistory.values())
        .reduce((sum, count) => sum + count, 0)
        .toString(),
    };

    if (changedFieldsCount > 0) {
      basePayload.changed_fields = JSON.stringify(
        changedFields.map((field) => ({ field_id: field })),
      );
    }

    if (addedFieldsCount > 0) {
      basePayload.added_fields = JSON.stringify(
        addedFields.map((field) => ({ field_id: field })),
      );
    }

    if (fieldErrorHistory.size > 0) {
      basePayload.error_fields = JSON.stringify(
        Array.from(fieldErrorHistory.entries()).map(([field_id, count]) => ({
          field_id,
          error_count: count,
        })),
      );
    }

    this.push('form_errors', basePayload);
  }

  // Get common abandon payload
  private getCommonAbandonPayload(
    offeringId: string,
    currentFormValues: FormValues,
    formControls: Record<string, AbstractControl>,
    offeringType: OfferingType,
    formId: string = 'offering_form',
  ): any {
    const { changedFields, addedFields } =
      this.getFieldChanges(currentFormValues);
    const changedFieldsCount = changedFields.length;
    const addedFieldsCount = addedFields.length;
    const totalFields = this.calculateTotalFields(currentFormValues);
    const unchangedFieldsCount =
      totalFields - changedFieldsCount - addedFieldsCount;
    const { errorFieldsCount, errorFields } = this.getErrorFields(formControls);

    const basePayload: any = {
      offering_id: offeringId,
      offering_type: offeringType,
      form_id: formId,
      fields_count: totalFields.toString(),
      unchanged_fields_count: unchangedFieldsCount.toString(),
      changed_fields_count: changedFieldsCount.toString(),
      added_fields_count: addedFieldsCount.toString(),
      error_fields_count: errorFieldsCount.toString(),
    };

    if (changedFieldsCount > 0) {
      basePayload.changed_fields = JSON.stringify(
        changedFields.map((field) => ({ field_id: field })),
      );
    }

    if (addedFieldsCount > 0) {
      basePayload.added_fields = JSON.stringify(
        addedFields.map((field) => ({ field_id: field })),
      );
    }

    if (errorFieldsCount > 0) {
      basePayload.error_fields = JSON.stringify(errorFields);
    }

    return basePayload;
  }

  // Push form abandon navigate event
  pushFormAbandonNavigateEvent(
    offeringId: string,
    currentFormValues: FormValues,
    formControls: Record<string, AbstractControl>,
    offeringType: OfferingType,
    formId: string = 'offering_form',
  ): void {
    const payload = {
      event_name: 'form_abandon_navigate',
      ...this.getCommonAbandonPayload(
        offeringId,
        currentFormValues,
        formControls,
        offeringType,
        formId,
      ),
    };

    this.push('form_abandon_navigate', payload);
  }

  // Push form abandon close event
  pushFormAbandonCloseEvent(
    offeringId: string,
    currentFormValues: FormValues,
    formControls: Record<string, AbstractControl>,
    offeringType: OfferingType,
    formId: string = 'offering_form',
  ): void {
    const payload = {
      event_name: 'form_abandon_close',
      ...this.getCommonAbandonPayload(
        offeringId,
        currentFormValues,
        formControls,
        offeringType,
        formId,
      ),
    };

    this.push('form_abandon_close', payload);
  }

  // Reset service state (useful when component is destroyed or form is reset)
  reset(): void {
    this.errorCounts = {};
    this.originalFormValues = null;
    this._cachedChangedFields = null;
    this._cachedAddedFields = null;
    this.changedFields = [];
    this.addedFields = [];
  }

  // Set original knowledge values
  setOriginalKnowledgeValues(knowledgeValues: KnowledgeValues): void {
    this.originalKnowledgeValues = knowledgeValues;
    this._cachedKnowledgeSectionChanges = null;
  }

  // Invalidate knowledge cache
  invalidateKnowledgeCache(): void {
    this._cachedKnowledgeSectionChanges = null;
  }

  // Get section changes with caching
  private getKnowledgeSectionChanges(
    currentKnowledgeValues: KnowledgeValues,
  ): KnowledgeSectionChange[] {
    if (this._cachedKnowledgeSectionChanges !== null) {
      return this._cachedKnowledgeSectionChanges;
    }

    if (!this.originalKnowledgeValues) {
      this._cachedKnowledgeSectionChanges = [];
      return [];
    }

    const changes: KnowledgeSectionChange[] = [];

    // Check all sections from current values
    for (const currentSection of currentKnowledgeValues.sections) {
      const originalSection = this.originalKnowledgeValues.sections.find(
        (s) => s.name === currentSection.name,
      );

      if (!originalSection) {
        // This shouldn't happen, but handle it just in case
        if (currentSection.isVisible && currentSection.rowCount > 0) {
          changes.push({
            section: currentSection.name,
            rows_added: currentSection.rowCount,
            visibility_changed_to: 'enabled',
          });
        }
        continue;
      }

      // Check if visibility changed
      const visibilityChanged =
        originalSection.isVisible !== currentSection.isVisible;

      // Only process visible sections or sections that changed visibility
      if (!currentSection.isVisible && !visibilityChanged) {
        continue; // Skip sections that are invisible and haven't changed
      }

      const rowDiff = currentSection.rowCount - originalSection.rowCount;
      const descriptionChanged =
        currentSection.description !== undefined &&
        originalSection.description !== undefined &&
        currentSection.description !== originalSection.description;

      // Check if row content changed (for rows that exist in both)
      const rowsEdited = currentSection.isVisible
        ? this.countEditedRows(
            originalSection.data || [],
            currentSection.data || [],
          )
        : 0;

      // Determine what changed
      const hasRowChanges = rowDiff !== 0 || rowsEdited > 0;
      const hasDescriptionChange = descriptionChanged;
      const hasAnyChange =
        hasRowChanges || hasDescriptionChange || visibilityChanged;

      if (hasAnyChange) {
        const change: KnowledgeSectionChange = {
          section: currentSection.name,
        };

        if (visibilityChanged) {
          change.visibility_changed_to = currentSection.isVisible
            ? 'enabled'
            : 'disabled';
        }

        // Only track row changes if section is visible or became visible
        if (currentSection.isVisible) {
          if (rowDiff > 0) {
            change.rows_added = rowDiff;
          } else if (rowDiff < 0) {
            change.rows_deleted = Math.abs(rowDiff);
          }

          if (rowsEdited > 0) {
            change.rows_changed = rowsEdited;
          }

          if (descriptionChanged) {
            change.description_changed = true;
          }
        }

        changes.push(change);
      }
    }

    this._cachedKnowledgeSectionChanges = changes;
    return changes;
  }

  // Helper method to count edited rows
  private countEditedRows(originalData: any[], currentData: any[]): number {
    const minLength = Math.min(originalData.length, currentData.length);
    let editedCount = 0;

    for (let i = 0; i < minLength; i++) {
      // Compare row content using JSON stringify
      if (JSON.stringify(originalData[i]) !== JSON.stringify(currentData[i])) {
        editedCount++;
      }
    }

    return editedCount;
  }

  // Calculate knowledge metrics
  private calculateKnowledgeMetrics(currentKnowledgeValues: KnowledgeValues): {
    totalSectionsAvailable: number;
    totalRowsCount: number;
    changedSectionsCount: number;
    addedRowsCount: number;
    deletedRowsCount: number;
    visibilityChangedCount: number;
    availableSections: string[];
  } {
    const changes = this.getKnowledgeSectionChanges(currentKnowledgeValues);

    const visibleSections = currentKnowledgeValues.sections.filter(
      (s) => s.isVisible,
    );
    const totalRowsCount = visibleSections.reduce(
      (sum, s) => sum + s.rowCount,
      0,
    );

    // Count sections with additions
    const addedRowsCount = changes.filter(
      (c) => c.rows_added && c.rows_added > 0,
    ).length;

    // Count sections with deletions
    const deletedRowsCount = changes.filter(
      (c) => c.rows_deleted && c.rows_deleted > 0,
    ).length;

    // Count sections with changes (includes rows_changed or any modification)
    const changedSectionsCount = changes.filter(
      (c) => c.rows_changed || c.rows_added || c.rows_deleted,
    ).length;

    // Count sections with visibility changes
    const visibilityChangedCount = changes.filter(
      (c) => c.visibility_changed_to,
    ).length;

    const availableSections = visibleSections.map((s) => s.name);

    return {
      totalSectionsAvailable: visibleSections.length,
      totalRowsCount,
      changedSectionsCount,
      addedRowsCount,
      deletedRowsCount,
      visibilityChangedCount,
      availableSections,
    };
  }

  // Get error sections count
  private getErrorSectionsInfo(errorSections: string[]): {
    error_sections_count: number;
    error_sections?: string;
  } {
    return {
      error_sections_count: errorSections.length,
      ...(errorSections.length > 0 && {
        error_sections: JSON.stringify(errorSections),
      }),
    };
  }

  // Push knowledge form entry event
  pushKnowledgeFormEntryEvent(offeringId: string, knowledgeId: string): void {
    const payload = {
      event_name: 'form_entry',
      offering_id: offeringId,
      knowledge_id: knowledgeId,
      form_id: 'knowledge_form',
    };

    this.push('form_entry', payload);
  }

  // Push knowledge form success event
  pushKnowledgeFormSuccessEvent(
    offeringId: string,
    knowledgeId: string,
    currentKnowledgeValues: KnowledgeValues,
  ): void {
    const metrics = this.calculateKnowledgeMetrics(currentKnowledgeValues);
    const sectionChanges = this.getKnowledgeSectionChanges(
      currentKnowledgeValues,
    );

    const payload: any = {
      event_name: 'form_success',
      offering_id: offeringId,
      knowledge_id: knowledgeId,
      form_id: 'knowledge_form',
      total_sections_available: metrics.totalSectionsAvailable,
      available_sections: JSON.stringify(metrics.availableSections),
      total_rows_count: metrics.totalRowsCount,
      changed_sections_count: metrics.changedSectionsCount,
      added_rows_count: metrics.addedRowsCount,
      deleted_rows_count: metrics.deletedRowsCount,
      visibility_changed_count: metrics.visibilityChangedCount,
    };

    if (sectionChanges.length > 0) {
      payload.section_changes = JSON.stringify(sectionChanges);
    }

    this.push('form_success', payload);
  }

  // Push knowledge form error event
  pushKnowledgeFormErrorEvent(
    offeringId: string,
    knowledgeId: string,
    currentKnowledgeValues: KnowledgeValues,
    errorSections: string[],
  ): void {
    const metrics = this.calculateKnowledgeMetrics(currentKnowledgeValues);
    const sectionChanges = this.getKnowledgeSectionChanges(
      currentKnowledgeValues,
    );
    const errorInfo = this.getErrorSectionsInfo(errorSections);

    const payload: any = {
      event_name: 'form_errors',
      offering_id: offeringId,
      knowledge_id: knowledgeId,
      form_id: 'knowledge_form',
      total_sections_available: metrics.totalSectionsAvailable,
      available_sections: JSON.stringify(metrics.availableSections),
      total_rows_count: metrics.totalRowsCount,
      changed_sections_count: metrics.changedSectionsCount,
      added_rows_count: metrics.addedRowsCount,
      deleted_rows_count: metrics.deletedRowsCount,
      visibility_changed_count: metrics.visibilityChangedCount,
      ...errorInfo,
    };

    if (sectionChanges.length > 0) {
      payload.section_changes = JSON.stringify(sectionChanges);
    }

    this.push('form_errors', payload);
  }

  // Push knowledge draft save event
  pushKnowledgeDraftSaveEvent(
    offeringId: string,
    knowledgeId: string,
    currentKnowledgeValues: KnowledgeValues,
  ): void {
    const metrics = this.calculateKnowledgeMetrics(currentKnowledgeValues);
    const sectionChanges = this.getKnowledgeSectionChanges(
      currentKnowledgeValues,
    );

    const payload: any = {
      event_name: 'form_draft_save',
      offering_id: offeringId,
      knowledge_id: knowledgeId,
      form_id: 'knowledge_form',
      total_sections_available: metrics.totalSectionsAvailable,
      available_sections: JSON.stringify(metrics.availableSections),
      total_rows_count: metrics.totalRowsCount,
      changed_sections_count: metrics.changedSectionsCount,
      added_rows_count: metrics.addedRowsCount,
      deleted_rows_count: metrics.deletedRowsCount,
      visibility_changed_count: metrics.visibilityChangedCount,
    };

    if (sectionChanges.length > 0) {
      payload.section_changes = JSON.stringify(sectionChanges);
    }

    this.push('form_draft_save', payload);
  }

  // Push knowledge abandon navigate event
  pushKnowledgeAbandonNavigateEvent(
    offeringId: string,
    knowledgeId: string,
    currentKnowledgeValues: KnowledgeValues,
  ): void {
    const metrics = this.calculateKnowledgeMetrics(currentKnowledgeValues);
    const sectionChanges = this.getKnowledgeSectionChanges(
      currentKnowledgeValues,
    );

    const payload: any = {
      event_name: 'form_abandon_navigate',
      offering_id: offeringId,
      knowledge_id: knowledgeId,
      form_id: 'knowledge_form',
      total_sections_available: metrics.totalSectionsAvailable,
      available_sections: JSON.stringify(metrics.availableSections),
      total_rows_count: metrics.totalRowsCount,
      changed_sections_count: metrics.changedSectionsCount,
      added_rows_count: metrics.addedRowsCount,
      deleted_rows_count: metrics.deletedRowsCount,
      visibility_changed_count: metrics.visibilityChangedCount,
    };

    if (sectionChanges.length > 0) {
      payload.section_changes = JSON.stringify(sectionChanges);
    }

    this.push('form_abandon_navigate', payload);
  }

  // Push knowledge abandon close event
  pushKnowledgeAbandonCloseEvent(
    offeringId: string,
    knowledgeId: string,
    currentKnowledgeValues: KnowledgeValues,
  ): void {
    const metrics = this.calculateKnowledgeMetrics(currentKnowledgeValues);
    const sectionChanges = this.getKnowledgeSectionChanges(
      currentKnowledgeValues,
    );

    const payload: any = {
      event_name: 'form_abandon_close',
      offering_id: offeringId,
      knowledge_id: knowledgeId,
      form_id: 'knowledge_form',
      total_sections_available: metrics.totalSectionsAvailable,
      available_sections: JSON.stringify(metrics.availableSections),
      total_rows_count: metrics.totalRowsCount,
      changed_sections_count: metrics.changedSectionsCount,
      added_rows_count: metrics.addedRowsCount,
      deleted_rows_count: metrics.deletedRowsCount,
      visibility_changed_count: metrics.visibilityChangedCount,
    };

    if (sectionChanges.length > 0) {
      payload.section_changes = JSON.stringify(sectionChanges);
    }

    this.push('form_abandon_close', payload);
  }

  // Reset knowledge tracking
  resetKnowledge(): void {
    this.originalKnowledgeValues = null;
    this._cachedKnowledgeSectionChanges = null;
  }

  // ─── Web interactions (faroAction) ─────────────────────────────────────────

  // Emit a business-critical user interaction as a stateless Faro event.
  // External links get a distinct event name since their destination cannot be
  // followed after navigation.
  //
  // NOTE: we deliberately do NOT use `faro.api.startUserAction`. A User Action
  // attaches a document-wide MutationObserver and only settles once the DOM is
  // quiet for 100ms; on interactions that navigate and re-render (e.g. a search
  // submit) the DOM never goes quiet, the action never ends, and the tab
  // freezes. `pushEvent` carries the same payload with no observers/lifecycle.
  pushUserActionEvent(name: string, attributes: Record<string, string>): void {
    const eventName =
      attributes['linkType'] === 'external'
        ? 'faro.user.action.external'
        : 'faro.user.action';
    this.push(eventName, { actionName: name, ...attributes });
  }
}
